import { useEffect, useState } from 'react';
import { adminApi } from '../../../services/adminApi';

interface MaintenanceState {
  softLock: boolean;
  hardLock: boolean;
  message: string | null;
  setByAdminId?: string | null;
  setAt?: string;
}

export default function MaintenanceTab({
  state: propState,
  onChange,
}: {
  state?: MaintenanceState;
  onChange: () => void;
}) {
  const [state, setState] = useState<MaintenanceState | null>(propState ?? null);
  const [message, setMessage] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    const res = await adminApi.get<MaintenanceState>('/maintenance');
    setState(res.data);
    setMessage(res.data.message ?? '');
  }
  useEffect(() => {
    refresh();
  }, []);

  async function toggle(field: 'softLock' | 'hardLock', value: boolean) {
    setBusy(true);
    setErr(null);
    try {
      const body: any = { [field]: value };
      if (message !== (state?.message ?? '')) body.message = message || null;
      const res = await adminApi.post('/maintenance', body);
      setState(res.data.state);
      onChange();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function saveMessage() {
    setBusy(true);
    setErr(null);
    try {
      const res = await adminApi.post('/maintenance', { message: message || null });
      setState(res.data.state);
      onChange();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  if (!state) return <div className="text-gray-400">Loading\u2026</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-bold">Maintenance mode</h2>
        <p className="text-sm text-gray-400 mt-1">
          Two independent toggles. Both can be on at once. Admin endpoints stay
          available no matter what.
        </p>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {err}
        </div>
      )}

      <ToggleCard
        title="Soft lock"
        value={state.softLock}
        busy={busy}
        onToggle={(v) => {
          if (v && !confirm('Enable SOFT lock?\n\nBlocks new logins, signups, deposits, joining new games.\nWithdrawals and in-progress games still work.')) return;
          if (!v && !confirm('Disable soft lock?')) return;
          toggle('softLock', v);
        }}
        description="Blocks new logins, signups, deposits, and joining new games. Withdrawals and in-progress games still work. Use for routine fixes / suspicious-but-not-confirmed activity."
        tone="amber"
      />

      <ToggleCard
        title="Hard lock"
        value={state.hardLock}
        busy={busy}
        onToggle={(v) => {
          if (v && !confirm('Enable HARD lock?\n\nBlocks EVERYTHING except admin: no logins, no actions, no withdrawals, no deposits. Live games are paused (turn timers stop).')) return;
          if (!v && !confirm('Disable hard lock?')) return;
          toggle('hardLock', v);
        }}
        description="Blocks everything except admin endpoints. Live games halt. Use during confirmed exploit response."
        tone="red"
      />

      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
        <div className="text-sm font-semibold">Banner message (optional)</div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Shown to users on the lock screen. Eg. 'Paused while we investigate a withdrawal issue, back shortly.'"
          rows={3}
          className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm"
        />
        <button
          disabled={busy}
          onClick={saveMessage}
          className="px-3 py-1.5 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg disabled:opacity-50"
        >
          Save message
        </button>
      </div>

      {(state.softLock || state.hardLock) && (
        <div className="text-xs text-gray-500">
          Set at {state.setAt ? new Date(state.setAt).toLocaleString() : '\u2014'} by{' '}
          {state.setByAdminId ?? '\u2014'}.
        </div>
      )}
    </div>
  );
}

function ToggleCard({
  title,
  value,
  busy,
  onToggle,
  description,
  tone,
}: {
  title: string;
  value: boolean;
  busy: boolean;
  onToggle: (v: boolean) => void;
  description: string;
  tone: 'amber' | 'red';
}) {
  const toneClass =
    tone === 'red'
      ? value
        ? 'border-red-500/60 bg-red-500/10'
        : 'border-white/10 bg-white/5'
      : value
        ? 'border-amber-500/60 bg-amber-500/10'
        : 'border-white/10 bg-white/5';
  return (
    <div className={`border rounded-xl p-4 ${toneClass}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2">
            {title}
            {value && (
              <span className="text-xs px-2 py-0.5 rounded bg-red-500/30 text-red-200 border border-red-500/40">
                ACTIVE
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-1">{description}</div>
        </div>
        <button
          disabled={busy}
          onClick={() => onToggle(!value)}
          className={`px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50 ${
            value
              ? 'bg-white/10 hover:bg-white/20 border border-white/20'
              : 'bg-red-600/30 hover:bg-red-600/40 border border-red-500/50'
          }`}
        >
          {value ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
  );
}
